import React, { useEffect, useRef } from 'react';
import type { DrawTool } from '../hooks/useDrawCanvas';

interface DrawCanvasProps {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  tool: DrawTool;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onStartDrawing: (x: number, y: number) => void;
  onContinueDrawing: (x: number, y: number) => void;
  onEndDrawing: () => void;
  active?: boolean;
}

const DrawCanvas: React.FC<DrawCanvasProps> = ({
  canvasRef, tool, containerRef,
  onStartDrawing, onContinueDrawing, onEndDrawing, active,
}) => {
  // 同步 canvas 尺寸到容器
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;
    canvas.width = container.offsetWidth;
    canvas.height = container.offsetHeight;
  }, []);

  const isDrawing = !!active && (tool === 'brush' || tool === 'eraser');

  // 用 ref 包住最新的 callback，避免 useEffect 因 callback 重建而頻繁解綁重綁
  const startRef = useRef(onStartDrawing);
  const moveRef = useRef(onContinueDrawing);
  const endRef = useRef(onEndDrawing);
  startRef.current = onStartDrawing;
  moveRef.current = onContinueDrawing;
  endRef.current = onEndDrawing;

  // 原生 touch listener：React 17+ 把 onTouchStart/Move 註冊為 passive，
  // 在裡面呼叫 preventDefault 會被警告且失效。改用 addEventListener({ passive: false })。
  useEffect(() => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const getPos = (touch: Touch) => {
      const rect = canvas.getBoundingClientRect();
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    const handleStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      e.preventDefault();
      const p = getPos(t);
      startRef.current(p.x, p.y);
    };
    const handleMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      e.preventDefault();
      const p = getPos(t);
      moveRef.current(p.x, p.y);
    };
    const handleEnd = (e: TouchEvent) => {
      e.preventDefault();
      endRef.current();
    };

    canvas.addEventListener('touchstart', handleStart, { passive: false });
    canvas.addEventListener('touchmove', handleMove, { passive: false });
    canvas.addEventListener('touchend', handleEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleEnd, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', handleStart);
      canvas.removeEventListener('touchmove', handleMove);
      canvas.removeEventListener('touchend', handleEnd);
      canvas.removeEventListener('touchcancel', handleEnd);
    };
  }, [isDrawing, canvasRef]);

  const getPosFromMouse = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 rounded-xl"
      style={{
        pointerEvents: isDrawing ? 'auto' : 'none',
        cursor: tool === 'brush' ? 'crosshair' : tool === 'eraser' ? 'cell' : 'default',
        touchAction: 'none',
        zIndex: 20,
      }}
      onMouseDown={e => { const p = getPosFromMouse(e); onStartDrawing(p.x, p.y); }}
      onMouseMove={e => { const p = getPosFromMouse(e); onContinueDrawing(p.x, p.y); }}
      onMouseUp={onEndDrawing}
      onMouseLeave={onEndDrawing}
    />
  );
};

export default DrawCanvas;
